// ══════════════════════════════════════════════════════════════════════════════════════════════
//  EL BUSCA QUE TE LLEGA (B-875, 26-sep-2026)
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
//  El dueño: «su app empiece a vibrar constantemente hasta que se pare y sirve para indicar a esa persona que la buscas».
//  A pantalla completa, encima de todo: quién te busca y lo que te dice (el texto, o lo que dice su nota de voz), y
//  vibra (utils/vibrar) y suena con tu tono de llamadas (context/LlamadasContext · crearTono) SIN PARAR hasta que
//  contestas: «Voy», «Ahora no puedo» o «Silenciar». También para si quien te buscó lo cancela, si contestas desde otro
//  dispositivo, o a los 3 minutos. Debajo, el chat rápido con esa persona, por si hace falta decir algo más.
//
//  Llega por la conexión en directo de las llamadas (context/LlamadasContext lo reparte como el evento 'biango-busca'), y
//  al abrir la app se piden los que siguen sonando (GET /api/busca/pendientes): el aviso no se pierde por tener la app
//  cerrada un momento.
//
//  B-914 (28-sep-2026) · «debería salir en todos los dispositivos que esté conectado en ese momento, y si lo acepto en
//  uno, se quita en todos». La lista del SERVIDOR manda (colaDeBuscas · 'sincronizar'): se pide al abrir, cada vez que la
//  conexión en directo (re)conecta, al volver a la app, cuando llega un push del busca, cada 30 s con la pantalla a la
//  vista y cada 5 s mientras uno suena. Lo que allí ya no suena (contestado en otro dispositivo, cancelado o caducado) deja
//  de sonar aquí aunque se perdiera su aviso en directo, y sus notificaciones del sistema se quitan (utils/avisosDelBusca).
//
//  t398 (22-oct-2026) · «Voy» o «Silenciar» EN CUALQUIER DISPOSITIVO lo para en todos. «Silenciar» (antes «Lo he visto»)
//  deja de sonar y vibrar aquí y en sus otros dispositivos, y el servidor deja de repetir el aviso al móvil. Al empezar a
//  sonar, esta pantalla se lo dice al servidor (POST /busca/:id/sonando, con el identificador de este dispositivo y si es
//  un móvil, un ordenador o una tableta: utils/dispositivo), para que quien busca vea en cuántos dispositivos suena; y al
//  contestar dice desde cuál («Atendido por Bea en el móvil»).
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import { crearTono, leerTonoLocal } from '../../context/LlamadasContext'
import { empezarAVibrar } from '../../utils/vibrar'
import { Avatar } from '../ui'
import ChatRapido from './ChatRapido'
import { cerrarAvisosDelBusca } from '../../utils/avisosDelBusca'
import { idDelDispositivo, tipoDeDispositivo } from '../../utils/dispositivo'
import AvisoPantallaCompleta from './AvisoPantallaCompleta'
import { t, tx } from '../../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// La cola de buscas sonando, con lo que llega por la conexión. Pura: la mide la guardia.
export function colaDeBuscas(cola, evento) {
  const lista = Array.isArray(cola) ? cola : []
  const d = evento || {}
  if (d.tipo === 'entrante' && d.id != null) return [d, ...lista.filter(x => x.id !== d.id)]
  if ((d.tipo === 'parar' || d.tipo === 'respondido_aqui') && d.id != null) return lista.filter(x => x.id !== d.id)
  // B-914 · LO QUE DICE EL SERVIDOR MANDA: lo que allí ya no suena se quita aquí; lo que suena allí y aquí no estaba, entra.
  // Salvo lo que acaba de llegar en directo (`llego`, menos de 10 s): la lista pudo pedirse antes de que existiera.
  if (d.tipo === 'sincronizar' && Array.isArray(d.buscas)) {
    const ahora = Number(d.ahora) || 0
    const enServidor = new Set(d.buscas.map(x => x.id))
    const quedan = lista.filter(x => enServidor.has(x.id) || (x.llego && ahora && ahora - x.llego < 10000))
    const ya = new Set(quedan.map(x => x.id))
    return [...quedan, ...d.buscas.filter(x => !ya.has(x.id))]
  }
  if (d.tipo === 'pendientes' && Array.isArray(d.buscas)) {
    const ya = new Set(lista.map(x => x.id))
    return [...lista, ...d.buscas.filter(x => !ya.has(x.id))]
  }
  return lista
}

export default function BuscaEntrante() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [cola, setCola] = useState([])
  const actual = cola[0] || null
  // B-914 · pedir al servidor lo que sigue sonando: la usan los dos efectos de abajo.
  const sincronizarRef = useRef(() => {})
  // Hasta la primera respuesta del servidor no se sabe qué sigue sonando: hasta entonces no se quita ninguna notificación.
  const sincronizado = useRef(false)
  // t398 · los que se acaban de contestar AQUÍ: una lista del servidor pedida justo antes de contestar no los vuelve a
  // hacer sonar.
  const contestadosAqui = useRef(new Set())

  useEffect(() => {
    if (!user || user.rol === 'control_horario') return undefined
    let vivo = true
    const sincronizar = () => api.get('/busca/pendientes')
      .then(r => {
        if (!vivo) return
        const buscas = (r.data?.buscas || []).filter(b => !contestadosAqui.current.has(b.id))
        sincronizado.current = true
        setCola(c => colaDeBuscas(c, { tipo: 'sincronizar', buscas, ahora: Date.now() }))
        cerrarAvisosDelBusca(buscas.map(b => b.id))
      })
      .catch(() => {})
    sincronizarRef.current = sincronizar
    sincronizar()
    const on = (e) => {
      const d = (e && e.detail) || {}
      if (d.tipo === 'sincronizar') { sincronizar(); return }
      setCola(c => colaDeBuscas(c, d.tipo === 'entrante' ? { ...d, llego: Date.now() } : d))
    }
    window.addEventListener('biango-busca', on)
    const aLaVista = () => typeof document === 'undefined' || document.visibilityState !== 'hidden'
    const alVolver = () => { if (aLaVista()) sincronizar() }
    const conDoc = typeof document !== 'undefined' && typeof document.addEventListener === 'function'
    if (conDoc) document.addEventListener('visibilitychange', alVolver)
    window.addEventListener('focus', alVolver)
    const cada = setInterval(() => { if (aLaVista()) sincronizar() }, 30000)
    return () => {
      vivo = false
      sincronizarRef.current = () => {}
      window.removeEventListener('biango-busca', on)
      if (conDoc) document.removeEventListener('visibilitychange', alVolver)
      window.removeEventListener('focus', alVolver)
      clearInterval(cada)
    }
  }, [user?.id])

  // B-914 · las notificaciones del sistema de los buscas que ya no suenan, fuera (en el móvil y en el navegador).
  const idsCola = cola.map(x => x.id).join(',')
  useEffect(() => { if (sincronizado.current) cerrarAvisosDelBusca(cola.map(x => x.id)) }, [idsCola]) // eslint-disable-line react-hooks/exhaustive-deps

  // Mientras haya uno: vibrar y sonar sin parar, hasta que se conteste, se cancele o caduque.
  useEffect(() => {
    if (!actual) return undefined
    const pararVibrar = empezarAVibrar()
    let tono = null
    try { tono = crearTono(leerTonoLocal(user?.id)); tono.sonar() } catch { }
    const caduca = setTimeout(() => setCola(c => colaDeBuscas(c, { tipo: 'parar', id: actual.id })), Math.max(5000, Number(actual.sonar_ms) || 180000))
    // B-914 · mientras suena, cada 5 s se pregunta si sigue sonando: contestado en otro dispositivo, aquí para enseguida
    // aunque se haya perdido el aviso en directo.
    const vigila = setInterval(() => sincronizarRef.current(), 5000)
    // t398 · «me está sonando aquí»: quien busca ve en cuántos dispositivos suena.
    const dispositivo = idDelDispositivo()
    if (dispositivo) { try { const p = api.post('/busca/' + actual.id + '/sonando', { dispositivo, en: tipoDeDispositivo() }); if (p && p.catch) p.catch(() => {}) } catch { } }
    return () => { pararVibrar(); try { tono && tono.parar() } catch { } clearTimeout(caduca); clearInterval(vigila) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actual?.id])

  if (!actual) return null
  const responder = async (respuesta) => {
    const id = actual.id
    contestadosAqui.current.add(id)
    setCola(c => colaDeBuscas(c, { tipo: 'respondido_aqui', id }))
    try { await api.post('/busca/' + id + '/responder', { respuesta, en: tipoDeDispositivo() }) } catch { }
  }
  const dice = actual.texto || actual.transcripcion || null
  const boton = (fondo, color) => ({ padding: '12px 16px', borderRadius: 12, border: 'none', background: fondo, color, fontWeight: 800, fontSize: 15, cursor: 'pointer', flex: '1 1 140px' })
  return (
    <div role="alertdialog" aria-label={t('components.busca.BuscaEntrante.te_busca', '{{x}} te busca', { x: (actual.de?.nombre || t('components.busca.BuscaEntrante.alguien', 'Alguien')) })} data-busca-entrante
      style={{ position: 'fixed', inset: 0, zIndex: 3100, background: 'rgba(15,23,42,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 520, maxHeight: '100%', overflowY: 'auto', background: 'var(--surface)', borderRadius: 18, padding: 18, display: 'flex', flexDirection: 'column', gap: 14, boxShadow: '0 24px 60px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar src={actual.de?.avatar || null} nombre={actual.de?.nombre || '?'} size={64} />
          <span style={{ minWidth: 0 }}>
            <b style={{ display: 'block', fontSize: 20, lineHeight: 1.2 }}>{tx('components.busca.BuscaEntrante.te_busca', '{{x}} te busca', { x: actual.de?.nombre || t('components.busca.BuscaEntrante.alguien_del_equipo', 'Alguien del equipo') })}</b>
            {dice && <span data-busca-dice style={{ display: 'block', fontSize: 15, color: 'var(--text-secondary)', marginTop: 4, overflowWrap: 'anywhere' }}>{dice}</span>}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" data-responder="voy" onClick={() => responder('voy')} style={boton('#12a150', '#fff')}>{t('components.busca.BuscaEntrante.voy', 'Voy')}</button>
          <button type="button" data-responder="ahora_no" onClick={() => responder('ahora_no')} style={boton('#e08a3c', '#fff')}>{t('components.busca.BuscaEntrante.ahora_no_puedo', 'Ahora no puedo')}</button>
          <button type="button" data-responder="silenciar" onClick={() => responder('silenciar')} style={boton('var(--surface-2)', 'var(--text-primary)')}>{t('components.busca.BuscaEntrante.silenciar', 'Silenciar')}</button>
        </div>
        {/* B-1946 · en la app de Android, si el busca no puede salir a pantalla completa con el móvil bloqueado, se dice */}
        <AvisoPantallaCompleta />
        {actual.canal_id && <ChatRapido canalId={actual.canal_id} yoId={user?.id} alto={160} />}
        {actual.canal_id && (
          <button type="button" onClick={() => { responder('visto'); navigate('/chat?c=' + actual.canal_id) }}
            style={{ alignSelf: 'flex-start', border: 'none', background: 'transparent', color: 'var(--primary)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            {t('components.busca.BuscaEntrante.abrir_en_el_chat_de', 'Abrir en el chat de equipo')}
          </button>
        )}
        {cola.length > 1 && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{tx('components.busca.BuscaEntrante.y_mas_esperando', 'Y {{x}} más esperando.', { x: cola.length - 1 })}</span>}
      </div>
    </div>
  )
}
